import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { RadioButton } from "primereact/radiobutton";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { IBenefitCoverageRow } from "types/BenefitSelection/BenefitSelection";

import { PAYER_CLASS_OPTIONS, SERVICE_LINES } from "./constants";
import { IBenefitExceptionForm, nextLocalKey } from "./mapper";
import { coverageFor } from "./ServiceLinePicker";
import { Surface } from "./Surface";

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1.5rem minmax(11rem, 2fr) 1fr 1fr;
  gap: 0.75rem;
  padding: 0 0.7rem 0.25rem;
  /* The app's own DataTable header: not a caption, and never lighter than the rows beneath it. */
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-label);
`;

const Row = styled.label<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: 1.5rem minmax(11rem, 2fr) 1fr 1fr;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--primary-color)" : "var(--surface-200)"};
  border-radius: 6px;
  cursor: pointer;

  & + & {
    margin-top: 0.3rem;
  }
`;

/** A blank exception. NO service line — see `IBenefitExceptionForm.source`. */
export const emptyException = (): IBenefitExceptionForm => ({
  key: nextLocalKey(),
  payerClass: null,
  payerIds: [],
  planKeywords: [],
  source: null,
  tierPreference: null,
});

interface IExceptionDialogProps {
  visible: boolean;
  isNew: boolean;
  value: IBenefitExceptionForm;
  coverage: IBenefitCoverageRow[];
  checks: number;
  payerOptions: Array<{ label: string; value: number }>;
  loadingPayers: boolean;
  onSave: (exception: IBenefitExceptionForm) => void;
  onHide: () => void;
}

/**
 * CLE-2792 — one exception to the account's service line.
 *
 * 🚨 AN EXCEPTION MUST NAME A SCOPE, AND SAVE IS REFUSED WITHOUT ONE. An unscoped exception matches
 * every check, sits above the default in the ordered list and silently replaces it — with nothing on
 * the screen saying so. The API rejects it too; the guard is here as well so an editor is told at
 * the point of typing rather than by a 400.
 *
 * Counts inside this dialog are the ACCOUNT'S, not the exception's: the coverage endpoint groups by
 * service line over every check, and narrowing them to the payers this exception covers would need a
 * per-scope query nothing exposes yet. The column headers say what is counted; the sentence that
 * used to explain the population went with the info boxes in review round 1.
 */
export function ExceptionDialog({
  visible,
  isNew,
  value,
  coverage,
  checks,
  payerOptions,
  loadingPayers,
  onSave,
  onHide,
}: IExceptionDialogProps) {
  const [draft, setDraft] = useState<IBenefitExceptionForm>(value);
  const [scope, setScope] = useState<"class" | "payers">("class");

  useEffect(() => {
    if (!visible) return;
    setDraft(value);
    setScope(
      value.payerIds.length > 0 || value.planKeywords.length > 0
        ? "payers"
        : "class"
    );
  }, [visible, value]);

  const set = (patch: Partial<IBenefitExceptionForm>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const hasScope =
    scope === "class"
      ? Boolean(draft.payerClass)
      : draft.payerIds.length > 0 || draft.planKeywords.length > 0;

  const canSave = hasScope && draft.source !== null;

  const handleSave = () =>
    onSave(
      scope === "class"
        ? { ...draft, payerIds: [], planKeywords: [] }
        : { ...draft, payerClass: null }
    );

  return (
    <Dialog
      header={isNew ? "Add exception" : "Edit exception"}
      visible={visible}
      onHide={onHide}
      style={{ width: "min(46rem, 95vw)" }}
      data-testid="benefitSelection.exceptionDialog"
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            type="button"
            label="Cancel"
            className="p-button-text p-button-sm"
            data-testid="benefitSelection.exceptionDialog.cancel"
            onClick={onHide}
          />
          <Button
            className="p-button-sm"
            type="button"
            label={isNew ? "Add exception" : "Save exception"}
            disabled={!canSave}
            data-testid="benefitSelection.exceptionDialog.save"
            onClick={handleSave}
          />
        </div>
      }
    >
      <Surface className="flex flex-column gap-4">
        <div className="flex flex-column gap-2">
          <Label htmlFor="benefitSelection.exceptionDialog.scope">
            Applies to
          </Label>
          <div className="flex gap-4">
            <span className="flex align-items-center gap-2">
              <RadioButton
                inputId="exceptionScopeClass"
                checked={scope === "class"}
                onChange={() => setScope("class")}
              />
              <label htmlFor="exceptionScopeClass" className="text-sm">
                A payer class
              </label>
            </span>
            <span className="flex align-items-center gap-2">
              <RadioButton
                inputId="exceptionScopePayers"
                checked={scope === "payers"}
                onChange={() => setScope("payers")}
              />
              <label htmlFor="exceptionScopePayers" className="text-sm">
                Specific payers or plans
              </label>
            </span>
          </div>
        </div>

        {scope === "class" ? (
          <div className="flex flex-column gap-2" style={{ maxWidth: "22rem" }}>
            <Label htmlFor="benefitSelection.exceptionDialog.payerClass">
              Payer class
            </Label>
            <Dropdown
              id="benefitSelection.exceptionDialog.payerClass"
              data-testid="benefitSelection.exceptionDialog.payerClass"
              optionLabel="name"
              options={PAYER_CLASS_OPTIONS}
              placeholder="Choose a payer class"
              value={draft.payerClass ?? ""}
              onChange={(e) => set({ payerClass: e.value || null })}
            />
          </div>
        ) : (
          <div className="formgrid grid">
            <div className="field col-12 md:col-6">
              <Label htmlFor="benefitSelection.exceptionDialog.payerIds">
                Payers
              </Label>
              <MultiSelect
                id="benefitSelection.exceptionDialog.payerIds"
                data-testid="benefitSelection.exceptionDialog.payerIds"
                className="w-full"
                display="chip"
                filter
                options={payerOptions}
                disabled={loadingPayers}
                value={draft.payerIds}
                placeholder="Choose payers"
                onChange={(e) => set({ payerIds: e.value ?? [] })}
              />
            </div>
            <div className="field col-12 md:col-6">
              <Label htmlFor="benefitSelection.exceptionDialog.planKeywords">
                Plans / products (optional)
              </Label>
              <Chips
                id="benefitSelection.exceptionDialog.planKeywords"
                data-testid="benefitSelection.exceptionDialog.planKeywords"
                className="w-full"
                value={draft.planKeywords}
                placeholder="CHOICE PLUS"
                onChange={(e: ChipsChangeParams) =>
                  set({ planKeywords: e.value ?? [] })
                }
              />
            </div>
          </div>
        )}

        <div className="flex flex-column gap-2">
          <Label htmlFor="benefitSelection.exceptionDialog.source">
            Copay and co-insurance read from
          </Label>
          <HeaderRow>
            <span />
            <span>Service line</span>
            <span>Copay reported</span>
            <span>Co-ins reported</span>
          </HeaderRow>
          {SERVICE_LINES.map((line) => {
            const counts = coverageFor(coverage, line.value);
            return (
              <Row
                key={line.value}
                $selected={draft.source === line.value}
                htmlFor={`exceptionSource-${line.value}`}
                data-testid={`benefitSelection.exceptionDialog.source.${line.value}`}
              >
                <RadioButton
                  inputId={`exceptionSource-${line.value}`}
                  name="benefitSelectionExceptionSource"
                  checked={draft.source === line.value}
                  onChange={() => set({ source: line.value })}
                />
                <span className="flex flex-column">
                  <span className="font-medium text-sm">{line.label}</span>
                  <span className="text-xs text-color-secondary">
                    {line.pverifyNames}
                  </span>
                </span>
                <span className="text-sm">
                  {checks > 0 ? `${counts.copayReported} of ${checks}` : "—"}
                </span>
                <span className="text-sm">
                  {checks > 0
                    ? `${counts.coinsuranceReported} of ${checks}`
                    : "—"}
                </span>
              </Row>
            );
          })}
        </div>

        {!hasScope && (
          <span
            className="text-sm text-red-600"
            data-testid="benefitSelection.exceptionDialog.scopeWarning"
          >
            An exception must name a payer class, specific payers, or plan
            wording. Without a scope it would match every check and replace the
            service line above.
          </span>
        )}
      </Surface>
    </Dialog>
  );
}
